import { ArrowPathIcon, ScaleIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  v2Compare,
  v2GetRun,
  type RunDetail,
} from "../services/tradingAgentsV2";

const PROVIDERS = [
  { id: "openai", label: "OpenAI GPT-4", color: "border-green-400 bg-green-50" },
  { id: "anthropic", label: "Anthropic Claude", color: "border-purple-400 bg-purple-50" },
  { id: "google", label: "Google Gemini", color: "border-blue-400 bg-blue-50" },
];

export default function CompareAnalysis() {
  const [ticker, setTicker] = useState("AAPL");
  const [tradeDate, setTradeDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [selectedProviders, setSelectedProviders] = useState<string[]>([
    "openai",
    "anthropic",
    "google",
  ]);
  const [runIds, setRunIds] = useState<Record<string, string>>({});

  const compareMutation = useMutation({
    mutationFn: v2Compare,
    onSuccess: (data) => {
      setRunIds(data.runs);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticker.trim() || !tradeDate.trim()) return;
    if (selectedProviders.length < 2) return;
    compareMutation.mutate({
      ticker: ticker.trim().toUpperCase(),
      trade_date: tradeDate.trim(),
      providers: selectedProviders,
    });
  };

  const toggleProvider = (p: string) => {
    setSelectedProviders((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Multi-Model Comparison
        </h2>
        <p className="mb-4 text-sm text-gray-500">
          Run the same analysis through multiple AI models in parallel and
          compare their recommendations side-by-side.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Ticker"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              required
            />
            <Input
              label="Trade Date"
              type="date"
              value={tradeDate}
              onChange={(e) => setTradeDate(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label mb-2 block">LLM Providers (pick 2-3)</label>
            <div className="flex flex-wrap gap-2">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleProvider(p.id)}
                  className={`rounded-full border-2 px-4 py-1.5 text-sm font-medium transition-all ${
                    selectedProviders.includes(p.id)
                      ? `${p.color} shadow-sm`
                      : "border-gray-200 bg-white text-gray-500"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {compareMutation.isError && (
            <p className="text-sm text-red-600">
              {(compareMutation.error as Error).message}
            </p>
          )}
          <Button
            type="submit"
            isLoading={compareMutation.isPending}
            disabled={selectedProviders.length < 2}
            leftIcon={<ScaleIcon className="h-5 w-5" />}
          >
            Compare Models
          </Button>
        </form>
      </div>

      {/* Results columns */}
      {Object.keys(runIds).length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(runIds).map(([provider, runId]) => (
            <CompareColumn key={provider} provider={provider} runId={runId} />
          ))}
        </div>
      )}
    </div>
  );
}

function CompareColumn({ provider, runId }: { provider: string; runId: string }) {
  const provMeta = PROVIDERS.find((p) => p.id === provider);
  const detailQuery = useQuery({
    queryKey: ["v2-run", runId],
    queryFn: () => v2GetRun(runId),
    refetchInterval: (q) => {
      const s = (q.state.data as RunDetail | undefined)?.status;
      return s === "pending" || s === "running" ? 3000 : false;
    },
  });

  const detail = detailQuery.data;
  const state = (detail?.state ?? {}) as Record<string, unknown>;
  const decision = state.decision as {
    action?: string;
    confidence?: number;
    rationale?: string;
  } | undefined;

  const isRunning = detail?.status === "pending" || detail?.status === "running";

  return (
    <div className={`card border-2 ${provMeta?.color || "border-gray-200"}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {provMeta?.label || provider}
        </h3>
        {isRunning ? (
          <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
        ) : (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              detail?.status === "completed"
                ? "bg-green-100 text-green-700"
                : detail?.status === "failed"
                  ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-500"
            }`}
          >
            {detail?.status || "starting"}
          </span>
        )}
      </div>

      {decision ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span
              className={`text-2xl font-bold ${
                decision.action === "buy"
                  ? "text-green-600"
                  : decision.action === "sell"
                    ? "text-red-600"
                    : "text-gray-600"
              }`}
            >
              {(decision.action || "—").toUpperCase()}
            </span>
            {decision.confidence != null && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-600">
                {Math.round(decision.confidence * 100)}% conf
              </span>
            )}
          </div>
          {decision.rationale && (
            <p className="text-sm text-gray-600 line-clamp-4">
              {decision.rationale}
            </p>
          )}
        </div>
      ) : isRunning ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Agents deliberating...
        </div>
      ) : (
        <p className="text-sm text-gray-500">No result yet.</p>
      )}
    </div>
  );
}
